import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Welcome from './Welcome';
import SurveyQuestion from './SurveyQuestion';
import ThankYou from './ThankYou';
import type { Event, Question, Section, ResponseRow } from '../types';
import {
  getEventQuestions,
  ensureSession,
  getSession,
  updateSession,
  getResponses,
  deleteResponses,
} from '../services/api';
import { useParticipant } from '../hooks/useParticipant';

type Step = 'loading' | 'welcome' | 'question' | 'thankyou' | 'error';

export default function SurveyFlow() {
  const { id } = useParams<{ id: string }>();
  const { participant, loading: participantLoading, error: participantError } = useParticipant();
  const [step, setStep] = useState<Step>('loading');
  const [event, setEvent] = useState<Event | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<ResponseRow[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (participantLoading) return;
    if (participantError || !participant) {
      setError(participantError || 'Please enable cookies to take the survey.');
      setStep('error');
      return;
    }
    if (!id) {
      setError('Missing survey id');
      setStep('error');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getEventQuestions(id);
        if (cancelled) return;
        setEvent(data.event);
        const qs: Question[] = [];
        (data.sections || []).forEach((s: Section) => {
          (s.questions || []).forEach((q: Question) => qs.push(q));
        });
        setQuestions(qs);
        if (qs.length === 0) {
          setStep('thankyou');
          return;
        }
        await ensureSession(participant!.id, data.event.id);
        if (cancelled) return;
        const session = await getSession(participant!.id, data.event.id);
        const res = await getResponses(participant!.id, data.event.id);
        if (cancelled) return;
        setResponses(res);
        const lastQid = session?.last_question_id ?? null;
        const idx = lastQid ? qs.findIndex((q) => q.id === lastQid) + 1 : 0;
        const startIdx = Math.min(idx, qs.length);
        setQuestionIndex(startIdx);
        if (startIdx >= qs.length) setStep('thankyou');
        else setStep(startIdx === 0 ? 'welcome' : 'question');
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load survey');
          setStep('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id, participantLoading, participantError, participant]);

  const goToQuestions = useCallback(() => setStep('question'), []);

  const goNext = useCallback(() => {
    setQuestionIndex((i) => {
      if (i + 1 >= questions.length) {
        setStep('thankyou');
        return i;
      }
      return i + 1;
    });
  }, [questions.length]);

  const goSkip = useCallback(() => {
    setQuestionIndex((i) => {
      if (i + 1 >= questions.length) {
        setStep('thankyou');
        return i;
      }
      return i + 1;
    });
  }, [questions.length]);

  const handleReview = useCallback(async () => {
    if (!participant || !event) return;
    const res = await getResponses(participant.id, event.id);
    setResponses(res);
    setQuestionIndex(0);
    setStep('question');
  }, [participant, event]);

  const handleReset = useCallback(async () => {
    if (!participant || !event) return;
    try {
      await deleteResponses(participant.id, event.id);
      await updateSession(participant.id, event.id, { reset: true });
      setResponses([]);
      setQuestionIndex(0);
      setStep('welcome');
    } catch (e) {
      console.error(e);
    }
  }, [participant, event]);

  if (step === 'loading') {
    return (
      <div className="page page--loading">
        <div className="loading">Loading survey…</div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="page page--error">
        <div className="error-card">
          <h1>Something went wrong</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!event) return null;

  if (step === 'welcome') {
    return <Welcome eventName={event.name} onStart={goToQuestions} />;
  }

  if (step === 'thankyou') {
    return (
      <ThankYou
        eventName={event.name}
        onReview={handleReview}
        onReset={handleReset}
      />
    );
  }

  if (step === 'question' && participant && questions[questionIndex]) {
    return (
      <SurveyQuestion
        question={questions[questionIndex]}
        eventId={event.id}
        participantId={participant.id}
        questionIndex={questionIndex}
        totalQuestions={questions.length}
        existingResponses={responses}
        onNext={goNext}
        onSkip={goSkip}
        isLast={questionIndex === questions.length - 1}
      />
    );
  }

  return (
    <div className="page page--loading">
      <div className="loading">Loading…</div>
    </div>
  );
}
